import React, {useCallback, useEffect, useState} from "react";
import {decideListing, decideProject, getReviewQueue} from "../api/admin";
import {mediaUrl} from "../components/media";
import {formatPrice} from "../components/Fields";

const LABELS = {
    title: "Title", price: "Price", location: "Location", description: "Description", mainImages: "Photos", images: "Photos",
    coverImage: "Cover photo", amenities: "Amenities", reraNumber: "RERA number", bedrooms: "Bedrooms", sqft: "Area (sqft)",
};

const show = (value) => {
    if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
};

/** What a pending edit changes: field, live value, proposed value. */
const Diff = ({pending, live}) => (
    <table className="brk-table brk-diff">
        <thead><tr><th>Field</th><th>Live now</th><th>Proposed</th></tr></thead>
        <tbody>
        {Object.entries(pending).map(([k, v]) => (
            <tr key={k}><td>{LABELS[k] || k}</td><td>{show(live[k])}</td><td className="brk-strong">{show(v)}</td></tr>
        ))}
        </tbody>
    </table>
);

const Decision = ({onDecide}) => {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const decide = async (decision) => {
        if (decision !== "approve" && !note.trim()) {
            setError("Add a note telling the broker what to fix.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            await onDecide(decision, note.trim());
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };
    return (
        <div className="brk-decision">
            <textarea className="brk-input" rows={2} placeholder="Note to the broker (required to send back or reject)"
                      value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note to the broker"/>
            {error && <p className="brk-field__error" role="alert">{error}</p>}
            <div className="brk-actions">
                <button type="button" className="brk-btn brk-btn--primary brk-btn--sm" disabled={busy} onClick={() => decide("approve")}>Approve</button>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" disabled={busy} onClick={() => decide("request-changes")}>Request changes</button>
                <button type="button" className="brk-btn brk-btn--danger brk-btn--sm" disabled={busy} onClick={() => decide("reject")}>Reject</button>
            </div>
        </div>
    );
};

const BrokerLine = ({broker}) => broker && (
    <p className="brk-hint">
        {broker.companyName || broker.fullName} · {broker.accountType === "BUILDER" ? "Builder" : "Agent"} · {broker.mobile} · {broker.email}
        {broker.reraNumber ? ` · RERA ${broker.reraNumber}` : ""}
    </p>
);

const Photos = ({urls}) => urls?.length > 0 && (
    <div className="brk-review-photos">
        {urls.slice(0, 8).map((u) => <a key={u} href={mediaUrl(u)} target="_blank" rel="noreferrer"><img src={mediaUrl(u)} alt=""/></a>)}
        {urls.length > 8 && <span className="brk-hint">+{urls.length - 8} more</span>}
    </div>
);

const ListingCard = ({item, onDecided}) => {
    const l = item.listing;
    const pending = l.reviewStatus === "APPROVED" && l.pendingChanges;
    return (
        <li className="brk-card brk-review">
            <div className="brk-item__top">
                <h3 className="brk-review__title">{l.title}</h3>
                <span className="brk-badge brk-badge--amber">{pending ? "Edit to a live listing" : l.projectId ? "New unit type" : "New listing"}</span>
            </div>
            <BrokerLine broker={item.broker}/>
            <p>{[l.price, l.bhk, l.sqft && `${l.sqft} sqft`, l.location, l.projectName].filter(Boolean).join(" · ")}</p>
            {pending ? <Diff pending={l.pendingChanges} live={{...l, price: l.priceRaw}}/> : (
                <>
                    <Photos urls={[...(l.mainImages || []), ...(l.images || [])]}/>
                    {l.description && <p className="brk-review__desc">{l.description}</p>}
                    <p className="brk-hint">
                        {[l.reraNumber && `RERA ${l.reraNumber}`, l.amenities?.length && `${l.amenities.length} amenities`,
                            l.floorPlanUrl && "floor plan", l.brochureFile && "brochure"].filter(Boolean).join(" · ")}
                    </p>
                </>
            )}
            <Decision onDecide={async (d, note) => { await decideListing(l.id, d, note); await onDecided(); }}/>
        </li>
    );
};

const ProjectCard = ({item, onDecided}) => {
    const p = item.project;
    const pending = p.reviewStatus === "APPROVED" && p.pendingChanges;
    return (
        <li className="brk-card brk-review">
            <div className="brk-item__top">
                <h3 className="brk-review__title">{p.name}</h3>
                <span className="brk-badge brk-badge--amber">{pending ? "Edit to a live project" : "New project"}</span>
            </div>
            <BrokerLine broker={item.broker}/>
            <p>{[p.location, p.developerName, p.reraNumber && `RERA ${p.reraNumber}`, p.possessionStatus].filter(Boolean).join(" · ")}</p>
            {pending ? <Diff pending={p.pendingChanges} live={p}/> : (
                <>
                    <Photos urls={[p.coverImageUrl, ...(p.imageUrls || [])].filter(Boolean)}/>
                    {p.description && <p className="brk-review__desc">{p.description}</p>}
                    <table className="brk-table">
                        <thead><tr><th>Unit type</th><th>Price</th><th>Area</th></tr></thead>
                        <tbody>
                        {p.units.map((u) => (
                            <tr key={u.id}><td>{u.title}</td><td>{u.price || formatPrice(u.priceRaw)}</td><td>{u.sqft ? `${u.sqft} sqft` : "—"}</td></tr>
                        ))}
                        </tbody>
                    </table>
                    <p className="brk-hint">Approving publishes the project and these {p.units.length} unit types.</p>
                </>
            )}
            <Decision onDecide={async (d, note) => { await decideProject(p.id, d, note); await onDecided(); }}/>
        </li>
    );
};

/** Everything brokers have submitted: new listings, projects and unit types, and edits to live ones. */
const ReviewQueue = ({onAuthError}) => {
    const [queue, setQueue] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setError("");
        try {
            setQueue(await getReviewQueue());
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => { load(); }, [load]);

    if (error) return <div className="brk-notice brk-notice--error" role="alert">{error}</div>;
    if (!queue) return <p className="brk-empty">Loading…</p>;
    const total = queue.projects.length + queue.listings.length;
    return (
        <div>
            <div className="brk-card__head">
                <p className="brk-card__lead">{total ? `${total} waiting for review, oldest first.` : "Nothing to review. 🎉"}</p>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={load}>Refresh</button>
            </div>
            <ul className="brk-review-list">
                {queue.projects.map((item) => <ProjectCard key={`p${item.project.id}`} item={item} onDecided={load}/>)}
                {queue.listings.map((item) => <ListingCard key={`l${item.listing.id}`} item={item} onDecided={load}/>)}
            </ul>
        </div>
    );
};

export default ReviewQueue;
